// Klargo Design System — Dina fordon (/fordon), restyled to match
// Klargo-Reference/src/pages/VehiclesPage.tsx and VehicleDetail.tsx.
//
// There is no backend endpoint for a user's owned-vehicle register yet, so the page has two states:
//  - the vehicle is under one of this user's deals — open, or signed with the payment not yet
//    settled: it is shown as the vehicle record, in the reference's design, from the deal's car data
//    (see VehicleDealRecord and useActiveDealVehicle, which decides which deal that is);
//  - otherwise (no deal, expired, cancelled, or a deal whose payment is settled): the empty-state
//    register. The register the empty state promises — inspection, insurance, registered owner once
//    a purchase completes — needs the owned-vehicle backend that does not exist yet.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AuthShell from "../components/ds/AuthShell";
import VehicleDealRecord from "../components/ds/VehicleDealRecord";
import VehicleDocumentModal from "../components/ds/VehicleDocumentModal";
import ContractPreviewModal from "../components/ds/ContractPreviewModal";
import { useLanguage } from "../components/layout/LanguageSwitcher";
import useActiveDealVehicle from "../../utils/useActiveDealVehicle";

export default function VehiclesPage() {
  const { t, i18n } = useTranslation();
  const { t: tp, languageCode } = useLanguage();
  const navigate = useNavigate();
  const { loading, deal } = useActiveDealVehicle();

  // With an open deal the home page shows the ongoing-deal view, which is where the deal itself is shown.
  const viewDeal = () => {
    navigate("/user/ChooseAction");
  };

  // The purchase contract opens the same document modal as the transaction record (the real
  // contract view of this deal). The other three documents are demonstration papers.
  const [contractOpen, setContractOpen] = useState(false);
  const [paperDoc, setPaperDoc] = useState(null);

  return (
    <AuthShell t={t} activeKey="vehicles">
      {loading ? null : deal ? (
        <VehicleDealRecord
          t={t}
          tp={tp}
          language={languageCode || i18n.language}
          deal={deal}
          onViewDeal={viewDeal}
          onOpenContract={() => setContractOpen(true)}
          onOpenTransactions={() => navigate(`/affarshistorik/${deal.id}`)}
          onOpenDocument={(paper, fileName) => setPaperDoc({ paper, fileName })}
        />
      ) : (
        <section
          data-testid="vehicles-registry"
          className="ds-animate-page ds-auth-rail max-w-[1040px] pb-[var(--ds-page-bottom)] pt-[var(--ds-page-top)]"
        >
          <h1 className="ds-title-route m-0 mb-1.5">{tp("ds.registry.title")}</h1>
          <div className="ds-label">{tp("ds.registry.meta.empty")}</div>

          <div className="mt-[34px] w-full max-w-[720px] rounded-[14px] border border-ds-sheet-edge bg-ds-sheet p-[clamp(28px,4vw,44px)]">
            <h2 className="ds-title-chapter m-0 mb-2">{tp("ds.registry.empty.title")}</h2>
            <p className="ds-body m-0 mb-5">{tp("ds.registry.empty.body")}</p>
            <button
              type="button"
              onClick={() => navigate("/user/ChooseAction")}
              data-testid="vehicles-to-overview"
              className="ds-action-ink"
            >
              {tp("ds.registry.empty.cta")}
            </button>
          </div>
        </section>
      )}
      {deal ? (
        <>
          <ContractPreviewModal t={t} open={contractOpen} onClose={() => setContractOpen(false)} id={deal.id} />
          <VehicleDocumentModal t={t} tp={tp} paper={paperDoc?.paper} fileName={paperDoc?.fileName} onClose={() => setPaperDoc(null)} />
        </>
      ) : null}
    </AuthShell>
  );
}
